import React, { useState } from 'react';
import WelcomeMila from 'assets/images/welcome-mila-and-breathe-easy.png';
import WelcomeMilaWebp from 'assets/images/welcome-mila-and-breathe-easy.png?as=webp';
import LazyLoad from 'react-lazyload';
import { motion } from 'framer-motion';
import loadable from '@loadable/component';

const FadeIn = loadable(() => import('../common/FadeIn'));

const JobFaqItems = [
  {
    title: 'Ecom, Conversion Rate Optimization Lead',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>
        <p>As our conversion rate optimization lead, you’ll lead the creation of thoughtful, impactful user journeys for all air breathers to improve performance and drive growth. We’ll look to you to develop creative testing plans that are clearly measurable and scalable. In this cross-functional role, you’ll work closely with our marketing, design, mobile, and ecom teams. If you’re passionate about user experience and leveraging data to uncover and execute high-impact experiments, we’d love to meet!</p>
        <p className='no-margin'>
          <strong>Key Responsibilities</strong>
        </p>
        <ul>
          <li>Drive meaningful revenue growth by developing and executing testing plans focused on improving on-site conversion.</li>
          <li>Own the ongoing roadmap to improve the customer journey across Mila’s digital touchpoints through creative ideation, wireframing, analytics, and rapid testing. </li>
          <li>Exercise your creative muscle to develop thoughtful growth funnels, including but not limited to the development of landing pages, affiliate marketing programs, and referrals.</li>
          <li>Use your analytical expertise to uncover insights and develop an ongoing cadence for regular reporting. Experience working with Google Analytics/GA4, and Google Tag Manager is a must.</li>
        </ul>
        <p className='no-margin'>
          <strong>Key Qualifications</strong>
        </p>
        <ul>
          <li>3-5+ years of direct CRO and A/B testing experience, with a solid understanding of core digital marketing practices.</li>
          <li>Fluency in web analytics tools is a must, particularly with Google Analytics and the Google Tag Manager environment.</li>
          <li>Domain expertise in A/B testing tools and best practices (e.g., Optimizely).</li>
          <li>Experience working with high-growth consumer brands, especially those with custom web experiences.</li>
          <li>A curious, growth driven mindset. You’re always looking for ways to improve and delight, and putting the customer at the core of your process.</li>
          <li>Empathy — most importantly, you care, and love creating thoughtful, personal experiences at scale.</li>
        </ul>
        <p>
          To apply, please fill out this brief questionnaire -- you'll also have an opportunity to include your resume (PDFs only please):
          {' '}
          <a href='https://bit.ly/mila-cro-lead' target='_blank' rel='noopener noreferrer'>Apply here</a>
        </p>
      </div>
    ),
  },
  {
    title: 'Head of Mobile User Experience',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>
        <p>As the head of mobile user experience, you’ll lead the UX function and sit at a critical intersection between the design, engineering, marketing and customer service teams. You will be responsible for driving the overall success of feature releases: from prioritizing customer feedback, to developing roadmaps, design specs, coordinating design reviews, engineering sprints, beta reviews and QC.</p>

        <p>We are looking for candidates with a strong creative and design background who have a point of view, can communicate their ideas visually, and stresses over details. Product and project management experience highly preferred as this role sits at the intersection of design and product management.</p>

        <p className='no-margin'>
          <strong>Key Responsibilities</strong>
        </p>
        <ul>
          <li>Lead the Mobile Design UX capability with a creative, collaborative and deeply thoughtful approach to create the best possible user experience.</li>
          <li>Continuously gain new customer insights, user trends, and design practices to improve the user experience.</li>
          <li>Develop top-notch feature specs with mockups, wireframes and walkthroughs.</li>
          <li>Collaboration with the design, engineering, marketing and customer service teams on both planning, pre-release and post-release activities.</li>
          <li>Scheduling, project management and roadmapping.</li>
        </ul>

        <p className='no-margin'>
          <strong>Qualifications</strong>
        </p>

        <ul>
          <li>At least 5+ years experience in a UX design role or leadership role</li>
          <li>At least 3+ years experience of UI design experience</li>
          <li>At least 2+ years in project and/or product management experience</li>
          <li>A solid understanding and demonstration of user centered design principles</li>
          <li>Ability to collaborate effectively as part of a distributed, multi-disciplinary team</li>
          <li>Excellent interpersonal communication, analytic and leadership skills</li>
        </ul>
        <a href='https://www.linkedin.com/jobs/view/3222733495' target='_blank' rel='noopener noreferrer'>Apply here</a>
      </div>
    ),
  },
  {
    title: 'Director of Engineering',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>
        <p>As the Director of Engineering, you’ll lead our distributed team of 20+ smart and dedicated backend, mobile, frontend, ecom, firmware, control system and hardware engineers. Candidates with consumer IOT experience will be at the top of our list. This is a critical, high impact role where you will define and lead the strategic and technical direction, along with culture and execution.</p>

        <p className='no-margin'>
          <strong>Key Responsibilities</strong>
        </p>
        <ul>
          <li>You will be responsible for growing and developing an incredible team of hardworking backend, frontend, firmware and hardware engineers in delivering thoughtful, customer-focused features.</li>
          <li>
            You will work alongside product, manufacturing, marketing and customer service teams to create and maintain a portfolio of products that customers rave about.
          </li>
          <li>You will be a key member of the leadership team.</li>
          <li>You will drive and own the product roadmap: from short term feature releases, to mid-term product releases, to blue-sky R&D initiatives.</li>
          <li>You are comfortable with ambiguity inherent to startups and can decide and act without having the total picture, and solve problems that don’t have a clear solution.</li>
        </ul>

        <p className='no-margin'>
          <strong>Qualifications</strong>
        </p>
        <ul>
          <li>
            10+ years of overall experience in software engineering and prior experience at an early stage product or company that has seen considerable growth
          </li>
          <li>
            5+ years engineering management experience
          </li>
          <li>
            3+ years consumer IOT experience
          </li>
          <li>
            Experience recruiting, developing and retaining top-notch engineering talent
          </li>
          <li>
            Proven track record leading a multidisciplinary team.
          </li>
          <li>Experience as an early engineering leader, and desire to lead at an early stage again</li>
          <li>Ability to ship complex projects in an environment with multiple dependencies (manufacturing, hardware, firmware, backend, mobile)</li>
        </ul>
        <a href='https://www.linkedin.com/jobs/view/3223103462' target='_blank' rel='noopener noreferrer'>Apply here</a>
      </div>
    ),
  },
  {
    title: 'Care Squad Agent',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>
        <p>As a member of our beloved Care Squad, you’ll have the opportunity to make a meaningful impact on how families everywhere live — and breathe. In this role, you’ll work closely with customers around the world and be someone who can identify with 2 (or 3!) of the following:</p>

        <ul>
          <li>Part community manager/part social media support - you speak social and understand how to connect with customers on a 1:1 level as well as a 1: many scale</li>
          <li>Sales/Ops Whizzes - You pride yourself on being the ultimate product expert while being able to balance it out with helping address a customer's core questions/needs.</li>
          <li>Tech Savvy - you appreciate the role good, thoughtful tech can play in someone's life ... and love being the person to show them how to achieve that!</li>
        </ul>

        <p className='no-margin'>
          <strong>Key Responsibilities</strong>
        </p>
        <ul>
          <li>Answer inquiries in a timely, clear, and thoughtful manner.</li>
          <li>
            Work collaboratively with other squad members and cross-functional teams to be able to communicate knowledgeably across a multitude of areas including: shipping, operations, and product.
          </li>
          <li>
            Become the ultimate product expert with the ability to address both routine questions, while navigating more complex inquiries and escalating as needed.
          </li>
          <li>Ability to work a flex schedule that includes weekends.</li>
        </ul>
        <p className='no-margin'>
          <strong>Key Qualifications</strong>
        </p>
        <ul>
          <li>2+ years in customer service, operations, or related fields.</li>
          <li>
            Excellent grammar and writing skills — bad grammar drives you nuts, and you write effortlessly and communicate clearly (native-level fluency is a must).
          </li>
          <li>
            A love for tech and making lives healthier— explaining how tech works comes naturally to you, and you enjoy introducing new solutions to your family and friends.
          </li>
          <li>
            Attention to detail and a love for processes — both building and improving on them. Nothing slips through the cracks under your watch!
          </li>
          <li>
            Empathy — most importantly, you care, and treat every customer/interaction with respect and an infectiously positive attitude.
          </li>
          <li>Experience using Google Sheets, Zendesk, and Slack</li>
        </ul>
        <p>We're a 100% distributed team, and you should be comfortable working virtually, primarily on PDT. </p>
        <p>
          Last but not least, your personality is just as important as your skills. You will be representing our brand in every customer interaction. We build thoughtful products and strive to be a thoughtful company. Your role will demonstrate that same thoughtfulness, empathy and attention to detail in every customer interaction. People buy our products for the health of their loved ones and we take that seriously — we embrace personalized notes and building long-lasting relationships with our customers. On the flip side, our brand is young, fun and sorta quirky. We speak to our customers like friends so your emojis and giphy skills must be second to none. You have full authority to make them laugh. :)
        </p>
        <p>
          To apply, please fill out this brief questionnaire -- you'll also have an opportunity to include your resume (PDFs only please):
        </p>
        <p>
          <a href='https://bit.ly/care-squad-agent' target='_blank' rel='noopener noreferrer'>Apply here</a>
        </p>
      </div>
    ),
  },
  {
    title: 'Product Design Engineer',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>
        <p>As a product design engineer, you will own the mechanical architecture & detailed mechanical design of our next-generation indoor air quality products from concept into mass-production. We strive to bring thoughtfulness to the user experience in a product space that has been lacking. We’ll look for you to bring creative mechanical designs with solid engineering analysis to back it up.</p>
        <p>
          You’ll work closely with our industrial design, hardware, and firmware teams, as well as our manufacturing partners to develop product specifications and product designs, then drive analysis and prototype validation of those designs, finally moving to manufacturing, quality & reliability validation, and into production. You’ll also work with our R&D engineers to bring thoughtful new technical innovations into the products.
        </p>
        <p className='no-margin'>
          <strong>Key Qualifications</strong>
        </p>
        <ul>
          <li>
            5~10 years experience in mechanical / product design engineering (consumer electronics, automotive, small appliances, medical devices, consumer products, etc).
          </li>
          <li>
            Experience designing with high volume manufacturing processes (injection molding, compression molding, die casting, stamping, etc).
          </li>
          <li>
            Experience working with contract manufacturers. Experience with Asia supply base is preferred.
          </li>
          <li>Experience integrating electronics (PCBAs, sensors) strongly preferred.</li>
          <li>Excellent communication skills to work effectively & efficiently with a distributed team.</li>
          <li>Working knowledge of DFM/DFA, GD&T, tolerance analysis, failure analysis, design of experiment, FMEA, reliability testing.</li>
          <li>
            Experience with air/water products, HVAC, refrigerant systems, CFD, fan optimization, air quality instrumentation are each a plus.
          </li>
          <li> Bachelor’s in Mechanical Engineering (or equivalent).</li>
        </ul>
        <p>
          <a href='https://www.linkedin.com/jobs/view/3450891741/' target='_blank' rel='noopener noreferrer'>Apply here</a>
        </p>
      </div>
    ),
  },
  {
    title: 'Office Manager / People Operations',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>

        <p>The “office” manager / people ops is a unique role within our 100% remote organization, and will partner with our leadership team across multiple aspects of the brand as we scale and and help every family breathe easier — and healthier. The initiatives you tackle may shift on a regular basis, and some core areas you may work on include helping …</p>

        <ul>
          <li>Build out a recruiting process that allows us to scale our team.</li>
          <li>Build out a core program that ensures that all team members, no matter where they sit, feel part of our family.</li>
          <li>Build and improve on internal processes that drive performance and cross-functional alignment.</li>
          <li>Manage special projects and initiatives.</li>
        </ul>

        <p>
          In this role, you'll be someone who is both strategic and can dive down to the 1,000 foot level to help teams achieve our collective goals. High EQ is a must, as well as the adaptability to navigate fluently across different priorities.
        </p>

        <p className='no-margin'>
          <strong>Qualifications</strong>
        </p>
        <ul>
          <li>
            2+ years in a people ops related role
          </li>
          <li>Experience working with and scaling a rapidly growing team, ideally in consumer tech</li>
          <li>An ability to build rapport in a virtual environment, and work seamlessly across teams to break down silos </li>
        </ul>
        <p>
          To apply, please email us at
          {' '}
          <a href='mailto: joinus@milacares.com'>joinus@milacares.com</a>
          {' '}
          with your resume and a note on why you think this role would be a good fit.
        </p>
      </div>
    ),
  },
  {
    title: 'Dream Role [Open Call]',
    content: () => (
      <div className='accordion-item__content'>
        <p className='no-margin'>
          <strong>The Role</strong>
        </p>
        <p>
          Have a role you think you’d be an amazing fit for that you don’t see here? We’d love to hear! We’re always looking for thoughtful, caring air breathers who want to make a difference to join our crew. Please drop us a note at
          {' '}
          <a href='mailto: joinus@milacares.com'>joinus@milacares.com</a>
          {' '}
          with what you have in mind.
        </p>
      </div>
    ),
  },
];

const JobOpening = ({ innerRef }) => {
  const [ activeIndex, setActiveIndex ] = useState(null);

  const onClick = idx => {
    setActiveIndex(activeIndex === idx ? null : idx);
  };

  return (
    <section id='job-opening' className='join-faqs' ref={innerRef}>
      <div className='container'>
        <div className='join-faqs__card'>
          <div className='join-faqs__grid'>
            <div className='join-faqs__column'>
              <div className='join-faqs__content'>
                <h2 className='join-faqs__title'>
                  Join
                  <br />
                  the Mila fam.
                </h2>
                <div className='join-faqs__description'>
                  <p>
                    Check out the positions we’re currently hiring for. Have an idea for a role not
                    listed here?
                    {' '}
                    <a href='mailto:joinus@milacares.com'>Drop us a line</a>
                    {' '}
                    to say “hi” and tell us
                    about it. 🙂
                  </p>
                </div>
              </div>
            </div>
            <div className='join-faqs__column'>
              <figure className='join-faqs__figure'>
                <LazyLoad>
                  <FadeIn>
                    <picture>
                      <source srcSet={WelcomeMilaWebp} type='image/webp' />
                      <source srcSet={WelcomeMila} type='image/png' />
                      <img
                        src={WelcomeMila}
                        alt='welcome mila and breathe easy'
                      />
                    </picture>
                  </FadeIn>
                </LazyLoad>
              </figure>
            </div>
          </div>
          <div className='join-faqs__list'>
            {JobFaqItems?.map(({ title, content }, i) => (
              <div className='accordion-item'>
                {title
                  && (
                    <div className={`accordion-item__header ${activeIndex === i ? 'active' : ''}`} onClick={() => onClick(i)}>
                      <h3 className='accordion-item__title'>{title}</h3>
                      <svg className='icon icon-down-arrow-alt' viewBox='0 0 52 32'>
                        <path d='M1.704 1.704c2.153-2.153 5.572-2.266 7.858-0.34l0.37 0.34 16.25 16.248 16.25-16.248c2.153-2.153 5.572-2.266 7.858-0.34l0.37 0.34c2.153 2.153 2.266 5.572 0.34 7.858l-0.34 0.37-20.364 20.364c-2.153 2.153-5.572 2.266-7.858 0.34l-0.37-0.34-20.364-20.364c-2.272-2.272-2.272-5.956 0-8.228z' />
                      </svg>
                    </div>
                  )}
                {content()
                  && (
                    <motion.div
                      className='accordion-item__body'
                      initial={{ height: 0 }}
                      animate={{ height: activeIndex === i ? 'auto' : 0 }}
                      transition={{ duration: 0.5 }}
                      style={{ overflow: 'hidden' }}
                    >
                      {content()}
                    </motion.div>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobOpening;
